import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import parse from 'html-react-parser';
import ApiServices from '../../../../../platform/app/src/shared/ApiServices/ApiServices';
import ICDCodeViewer from '../ReportControls/ICDCodeViewer';
import { saveReport } from './utils/reportSaveUtils';
import { useScrollDetection } from './utils/scrollUtils';
import { ScrollArrow } from './components';
import './reportStyles.css';

interface FetchReportProps {
    onEdit?: () => void;
    onReportFetched?: (hasReport: boolean) => void;
    setIsFetching?: (loading: boolean) => void;
    setHasChanges?: (changed: boolean) => void;
}

function preprocessMedicalTemplateHtml(html: string) {
    if (!html) return html;

    return html.replace(
        /<\s*\/?\s*(?:T[0-9]|T1w|T2w|ADC|DWI|FLAIR|GRE|SWI|SE|IR)(?:[^>]*)>/gi,
        match => match.replace(/</g, '&lt;').replace(/>/g, '&gt;')
    );
}

const FetchReport = forwardRef(({ onReportFetched, setIsFetching, setHasChanges }: FetchReportProps, ref) => {
    const [reportContent, setReportContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const icdRef = useRef<any>(null);
    const [icdStatus, setIcdStatus] = useState({ loading: false, visible: false });

    const { showScrollArrow, scrollDown } = useScrollDetection(scrollRef, reportContent);

    useImperativeHandle(ref, () => ({
        async handleSaveReport() {
            if (!scrollRef.current) return;

            window.dispatchEvent(new CustomEvent('stopVoiceDictation', {
                detail: { reason: 'saving_report' }
            }));

            setLoading(true);
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const studyId = urlParams.get('studyId');
                if (!studyId) throw new Error('Missing study ID');

                await saveReport(scrollRef.current, {
                    studyId,
                    successMessage: 'Report updated successfully!',
                    errorMessage: 'Failed to update report'
                });
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        },

        getPlainText() {
            if (!scrollRef.current) return '';
            return scrollRef.current.innerText
                .replace(/[\n\r]/g, ' ')
                .replace(/\s{2,}/g, ' ')
                .trim();
        },
    }));

    const fetchReport = async () => {

        window.dispatchEvent(new CustomEvent('stopVoiceDictation', {
            detail: { reason: 'fetching_report' }
        }));

        setFetching(true);
        setIsFetching?.(true);

        try {
            const urlParams = new URLSearchParams(window.location.search);
            const studyId = urlParams.get('studyId');
            if (!studyId) return;

            const response = await ApiServices.getReport(studyId);

            if (response?.presigned_url) {
                const html = await fetch(response.presigned_url).then(r => r.text());
                const safeHtml = preprocessMedicalTemplateHtml(html);
                setReportContent(safeHtml);
                onReportFetched?.(true);
            } else {
                setReportContent(null);
                onReportFetched?.(false);
            }
        } catch (error) {
            setReportContent(null);
            onReportFetched?.(false);
        } finally {
            setFetching(false);
            setIsFetching?.(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const markChanged = () =>
            setHasChanges?.(true);

        container.addEventListener("input", markChanged);

        const observer = new MutationObserver(() => {
            const editableElements = container.querySelectorAll(
                "input, textarea, select, [contenteditable='true']"
            );

            editableElements.forEach(el => {
                const evt = el.tagName === "SELECT" ? "change" : "input";
                el.addEventListener(evt, markChanged, { once: true });
            });
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            container.removeEventListener("input", markChanged);
        };
    }, [reportContent]);

    return (
        <div className="relative flex h-full flex-col" id="report">

            {!fetching && reportContent && (
                <div className="flex justify-end mb-4">
                    <button
                        onClick={() => {
                            if (icdStatus.visible) {
                                icdRef.current?.hide();
                            } else {
                                icdRef.current?.extract();
                            }
                        }}
                        disabled={icdStatus.loading}
                        className="text-white rounded-full shadow-sm hover:shadow-md px-4 py-2.5 text-[15px] font-semibold transition-all bg-[var(--v2-primary-600)] hover:bg-[var(--v2-primary-500)]"
                    >
                        {icdStatus.loading
                            ? 'Extracting ICD-10 Codes...'
                            : icdStatus.visible
                                ? 'Hide ICD-10 Codes'
                                : 'Find ICD-10 Code'}
                    </button>
                </div>
            )}

            <ICDCodeViewer
                ref={icdRef}
                getText={() =>
                    scrollRef.current?.innerText
                        .replace(/[\n\r]/g, ' ')
                        .replace(/\s{2,}/g, ' ')
                        .trim() || ''
                }
                onStatusChange={setIcdStatus}
            />

            <div
                ref={scrollRef}
                contentEditable
                suppressContentEditableWarning={true}
                className="report-html-content editable-report-area flex-1 rounded-xl pt-4 custom-scrollbar border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] text-[var(--v2-text-primary)] shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                onInput={() => setHasChanges?.(true)}
                style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
            >
                {fetching ? (
                    <div className="flex h-full items-center justify-center">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                    </div>
                ) : reportContent ? (
                    <>{parse(reportContent)}</>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-4 text-[var(--v2-text-muted)]">
                        <p className="text-[15px]">No report available.</p>

                        <button
                            onClick={() =>
                                window.dispatchEvent(
                                    new CustomEvent('openCreateReportTab', {
                                        detail: { subTab: 'radai' },
                                    })
                                )
                            }
                            className="underline font-semibold text-[var(--v2-primary-500)] hover:text-[var(--v2-primary-400)] transition-colors"
                        >
                            Create New Report
                        </button>
                    </div>
                )}
            </div>

            <ScrollArrow visible={showScrollArrow} onScroll={scrollDown} />

            {loading && (
                <div
                    className="absolute inset-0 z-10 flex items-center justify-center rounded-md backdrop-blur-sm"
                    style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                >
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                </div>
            )}

        </div>
    );
});

export default FetchReport;
